import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AdaptiveTestReqAndRes,
    CommonMatricesRequest,
    MandatoryGateSettings,
    MatricesSettingsResponse,
    MatrixUpsertRequest,
    RequirementHistory,
    SubscribedPathway,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/api/matrices.api.ts';
import {
    getApiMatricesSettings,
    putApiMatricesSettingsAdaptive,
    putApiMatricesSettingsMandatoryGate,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/api/matrices.api.ts';
import {
    createApiMatrixRequirement,
    deleteApiMatrixRequirement,
    getApiMatrixRequirement,
    getApiTenantRequirementHistory,
    updateApiMatrixRequirement,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/api/matrices.api.ts';
import { REQUIREMENT_HISTORY_MOCK_DATA } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/model/mocks.ts';
import type { MatricesRow } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/table/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

interface MatricesError {
    message?: string;
    [k: string]: any;
}

interface MatricesState {
    error: MatricesError | null;

    requirementHistory: RequirementHistory[] | null;
    mandatoryGateSettings: MandatoryGateSettings | null;

    subscribedPathways: SubscribedPathway[];
    commonSubscribedPathways: PaginatedResponse<SubscribedPathway, EmptyResultsDescription> | null;
    editedSubscribedPathway: PaginatedResponse<SubscribedPathway, EmptyResultsDescription> | null;
    currentGetRequestId: string | null;

    editPreviewRow: MatricesRow | null;

    adaptiveTestSettings: AdaptiveTestReqAndRes | null;
    adaptiveTestsPolicy: boolean;
    atLeastOnePreTestIsActive: boolean;

    isGetRequirementHistoryPending: boolean;
    isGetMandatoryGatePending: boolean;
    isUpdateMandatoryGatePending: boolean;

    isGetMatrixPathwayRequirementsPending: boolean;
    isCreateMatrixPathwayRequirementPending: boolean;
    isUpdateMatrixPathwayRequirementPending: boolean;
    isDeleteMatrixPathwayRequirementPending: boolean;
    isUpdateAdaptiveTestPending: boolean;
}

const initialState: MatricesState = {
    error: null,

    // TODO: del when back-end ready
    requirementHistory: REQUIREMENT_HISTORY_MOCK_DATA, // null
    mandatoryGateSettings: null,

    subscribedPathways: [],
    commonSubscribedPathways: null,
    editedSubscribedPathway: null,
    currentGetRequestId: null,

    editPreviewRow: null,

    adaptiveTestSettings: null,
    adaptiveTestsPolicy: false,
    atLeastOnePreTestIsActive: false,

    isGetRequirementHistoryPending: false,
    isGetMandatoryGatePending: false,
    isUpdateMandatoryGatePending: false,

    isGetMatrixPathwayRequirementsPending: false,
    isCreateMatrixPathwayRequirementPending: false,
    isUpdateMatrixPathwayRequirementPending: false,
    isDeleteMatrixPathwayRequirementPending: false,
    isUpdateAdaptiveTestPending: false,
};

export const getTenantRequirementHistory = createAsyncThunk<
    PaginatedResponse<RequirementHistory, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; payload: CommonMatricesRequest },
    { rejectValue: MatricesError }
>('matrices/getTenantRequirementHistory', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiTenantRequirementHistory(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getMatrixPathwayRequirement = createAsyncThunk<
    PaginatedResponse<SubscribedPathway, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; payload: CommonMatricesRequest },
    { rejectValue: MatricesError }
>('matrices/getMatrixPathwayRequirement', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiMatrixRequirement(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createMatrixPathwayRequirement = createAsyncThunk<
    PaginatedResponse<SubscribedPathway, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; pathwayId: number; payload: MatrixUpsertRequest },
    { rejectValue: MatricesError }
>('matrices/createMatrixPathwayRequirement', ({ clientId, tenantAlias, pathwayId, payload }, { rejectWithValue }) =>
    createApiMatrixRequirement(clientId, tenantAlias, pathwayId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateMatrixPathwayRequirement = createAsyncThunk<
    PaginatedResponse<SubscribedPathway, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; id: number; payload: MatrixUpsertRequest },
    { rejectValue: MatricesError }
>('matrices/updateMatrixPathwayRequirement', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiMatrixRequirement(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteMatrixPathwayRequirement = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: MatricesError }
>('matrices/deleteMatrixPathwayRequirement', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiMatrixRequirement(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getMatricesSettings = createAsyncThunk<
    MatricesSettingsResponse,
    { clientId: number; tenantAlias: string },
    { rejectValue: MatricesError }
>('matrices/getMatricesSettings', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    getApiMatricesSettings(clientId, tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const putMatricesSettingsMandatoryGate = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string; payload: MandatoryGateSettings },
    { rejectValue: MatricesError }
>('matrices/putMatricesSettingsMandatoryGate', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    putApiMatricesSettingsMandatoryGate(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const putMatricesSettingsAdaptive = createAsyncThunk<
    AdaptiveTestReqAndRes,
    { clientId: number; tenantAlias: string; payload: AdaptiveTestReqAndRes },
    { rejectValue: MatricesError }
>('matrices/putMatricesSettingsAdaptive', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    putApiMatricesSettingsAdaptive(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const matricesSlice = createSlice({
    name: 'matrices',
    initialState,
    reducers: {
        setMatrixEditPreviewRow: (state, action: PayloadAction<{ sourceRowKey: string; row: MatricesRow }>) => {
            state.editPreviewRow = action.payload.row;
        },
        clearMatrixEditPreviewRow: (state) => {
            state.editPreviewRow = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getTenantRequirementHistory.pending, (state) => {
            state.isGetRequirementHistoryPending = true;
            state.error = null;
        });
        builder.addCase(getTenantRequirementHistory.fulfilled, (state, action) => {
            state.isGetRequirementHistoryPending = false;
            state.requirementHistory = action.payload.data;
        });
        builder.addCase(getTenantRequirementHistory.rejected, (state, action) => {
            state.isGetRequirementHistoryPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getMatrixPathwayRequirement.pending, (state, action) => {
            state.isGetMatrixPathwayRequirementsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getMatrixPathwayRequirement.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetMatrixPathwayRequirementsPending = false;
            state.commonSubscribedPathways = action.payload;
            state.currentGetRequestId = null;

            state.subscribedPathways = action.payload.data;
            state.atLeastOnePreTestIsActive = false;
        });
        builder.addCase(getMatrixPathwayRequirement.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetMatrixPathwayRequirementsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
            state.commonSubscribedPathways = null;
            state.subscribedPathways = [];
            state.atLeastOnePreTestIsActive = false;
        });

        builder.addCase(createMatrixPathwayRequirement.pending, (state) => {
            state.isCreateMatrixPathwayRequirementPending = true;
            state.error = null;
        });
        builder.addCase(createMatrixPathwayRequirement.fulfilled, (state, action) => {
            state.isCreateMatrixPathwayRequirementPending = false;
            state.editedSubscribedPathway = action.payload;
        });
        builder.addCase(createMatrixPathwayRequirement.rejected, (state, action) => {
            state.isCreateMatrixPathwayRequirementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateMatrixPathwayRequirement.pending, (state) => {
            state.isUpdateMatrixPathwayRequirementPending = true;
            state.error = null;
        });
        builder.addCase(updateMatrixPathwayRequirement.fulfilled, (state, action) => {
            state.isUpdateMatrixPathwayRequirementPending = false;
            state.editedSubscribedPathway = action.payload;
        });
        builder.addCase(updateMatrixPathwayRequirement.rejected, (state, action) => {
            state.isUpdateMatrixPathwayRequirementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteMatrixPathwayRequirement.pending, (state) => {
            state.isDeleteMatrixPathwayRequirementPending = true;
            state.error = null;
        });
        builder.addCase(deleteMatrixPathwayRequirement.fulfilled, (state) => {
            state.isDeleteMatrixPathwayRequirementPending = false;
        });
        builder.addCase(deleteMatrixPathwayRequirement.rejected, (state, action) => {
            state.isDeleteMatrixPathwayRequirementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getMatricesSettings.pending, (state) => {
            state.isGetMandatoryGatePending = true;
            state.isUpdateAdaptiveTestPending = true;
            state.error = null;
        });
        builder.addCase(getMatricesSettings.fulfilled, (state, action) => {
            state.isGetMandatoryGatePending = false;
            state.isUpdateAdaptiveTestPending = false;
            state.mandatoryGateSettings = action.payload.tenantMandatoryGateSettings;
            state.adaptiveTestSettings = action.payload.tenantAdaptiveSettingsDto;
            state.adaptiveTestsPolicy = action.payload.tenantAdaptiveSettingsDto.enabled;
        });
        builder.addCase(getMatricesSettings.rejected, (state, action) => {
            state.isGetMandatoryGatePending = false;
            state.isUpdateAdaptiveTestPending = false;
            state.error = extractError(action);
        });

        builder.addCase(putMatricesSettingsMandatoryGate.pending, (state) => {
            state.isUpdateMandatoryGatePending = true;
            state.error = null;
        });
        builder.addCase(putMatricesSettingsMandatoryGate.fulfilled, (state, action) => {
            state.isUpdateMandatoryGatePending = false;
            state.mandatoryGateSettings = action.meta.arg.payload;
        });
        builder.addCase(putMatricesSettingsMandatoryGate.rejected, (state, action) => {
            state.isUpdateMandatoryGatePending = false;
            state.error = extractError(action);
        });

        builder.addCase(putMatricesSettingsAdaptive.pending, (state) => {
            state.isUpdateAdaptiveTestPending = true;
            state.error = null;
        });
        builder.addCase(putMatricesSettingsAdaptive.fulfilled, (state, action) => {
            state.isUpdateAdaptiveTestPending = false;
            state.adaptiveTestSettings = action.payload;
            state.adaptiveTestsPolicy = action.payload.enabled;
        });
        builder.addCase(putMatricesSettingsAdaptive.rejected, (state, action) => {
            state.isUpdateAdaptiveTestPending = false;
            state.error = extractError(action);
        });
    },
});
export const { setMatrixEditPreviewRow, clearMatrixEditPreviewRow } = matricesSlice.actions;

export default matricesSlice.reducer;
